import React, { useCallback, useEffect, useRef, useState } from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { Autocomplete, Box, Button, ClickAwayListener, Popper, TextField, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useDevice } from "hooks";
import { IDealsWithInvestmentDetails } from "store/types";

interface DealNameSelectorProps {
  deals: IDealsWithInvestmentDetails[];
  selectedDealId: number | undefined;
  onDealChange: (deal: IDealsWithInvestmentDetails) => void;
}

export const DealNameSelector: React.FC<DealNameSelectorProps> = ({ deals, selectedDealId, onDealChange }) => {
  // Mobile: custom fixed positioning
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 250 });

  // Desktop: Popper-based
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const popperRef = useRef<HTMLDivElement | null>(null);
  const { isMobile } = useDevice();

  const selectedDeal = deals.find((deal) => deal.id === selectedDealId);
  const dealTitle = selectedDeal?.title || "Select deal";

  // Calculate dropdown position relative to button
  const updatePosition = useCallback(() => {
    if (!buttonRef.current || !isOpen) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const dropdownWidth = 250;
    const dropdownHeight = 300; // Approximate max height
    const gap = 4;

    let left = buttonRect.left;
    let top = buttonRect.bottom + gap;

    // Adjust horizontal position to fit in viewport
    if (left + dropdownWidth > viewportWidth) {
      left = viewportWidth - dropdownWidth - 8; // 8px padding from edge
    }
    if (left < 8) {
      left = 8;
    }

    // Adjust vertical position if dropdown would go below viewport
    if (top + dropdownHeight > viewportHeight) {
      // Try to show above button
      const spaceAbove = buttonRect.top;
      if (spaceAbove > dropdownHeight) {
        top = buttonRect.top - dropdownHeight - gap;
      } else {
        // If not enough space above, limit height
        top = buttonRect.bottom + gap;
      }
    }

    setPosition({
      top,
      left,
      width: dropdownWidth
    });
  }, [isOpen]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isMobile) {
      setIsOpen(true);
      // Update position after opening
      setTimeout(() => {
        updatePosition();
      }, 0);
    } else {
      setAnchorEl(event.currentTarget);
      buttonRef.current = event.currentTarget;
    }
  };

  const handleClose = () => {
    if (isMobile) {
      setIsOpen(false);
    } else {
      setAnchorEl(null);
      buttonRef.current = null;
    }
  };

  const open = isMobile ? isOpen : Boolean(anchorEl);

  // Auto-scroll on mobile when dropdown opens
  useEffect(() => {
    if (isOpen && isMobile && buttonRef.current) {
      setTimeout(() => {
        const button = buttonRef.current;
        if (button) {
          button.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest"
          });
        }
      }, 100);
    }
  }, [isOpen, isMobile]);

  // Disable body scroll on desktop when dropdown is open
  useEffect(() => {
    if (!isMobile && open) {
      // Store original overflow value
      const originalBodyOverflow = document.body.style.overflow;
      const originalBodyPosition = document.body.style.position;
      const originalBodyWidth = document.body.style.width;
      const originalBodyTop = document.body.style.top;
      const originalHtmlOverflow = document.documentElement.style.overflow;

      // Get current scroll position
      const scrollY = window.scrollY;

      // Disable scroll on body and html
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.top = `-${scrollY}px`;
      document.documentElement.style.overflow = "hidden";

      // Also prevent scroll on scrollable parent containers
      const getScrollableParents = (element: HTMLElement | null): HTMLElement[] => {
        const parents: HTMLElement[] = [];
        let current = element?.parentElement;

        while (current) {
          const style = window.getComputedStyle(current);
          const overflow = style.overflow + style.overflowY + style.overflowX;
          if (/(auto|scroll)/.test(overflow)) {
            parents.push(current);
          }
          current = current.parentElement;
        }

        return parents;
      };

      const scrollableParents = buttonRef.current ? getScrollableParents(buttonRef.current) : [];
      const originalParentOverflows = scrollableParents.map((parent) => ({
        element: parent,
        overflow: parent.style.overflow,
        overflowY: parent.style.overflowY,
        overflowX: parent.style.overflowX
      }));

      // Disable scroll on all scrollable parents
      scrollableParents.forEach((parent) => {
        parent.style.overflow = "hidden";
      });

      // Prevent wheel and touchmove events
      const preventScroll = (e: Event) => {
        e.preventDefault();
      };

      const preventScrollOptions = { passive: false } as EventListenerOptions;
      window.addEventListener("wheel", preventScroll, preventScrollOptions);
      window.addEventListener("touchmove", preventScroll, preventScrollOptions);

      return () => {
        // Restore original styles
        document.body.style.overflow = originalBodyOverflow;
        document.body.style.position = originalBodyPosition;
        document.body.style.width = originalBodyWidth;
        document.body.style.top = originalBodyTop;
        document.documentElement.style.overflow = originalHtmlOverflow;

        // Restore scroll on parent containers
        originalParentOverflows.forEach(({ element, overflow, overflowY, overflowX }) => {
          element.style.overflow = overflow;
          element.style.overflowY = overflowY;
          element.style.overflowX = overflowX;
        });

        // Remove event listeners
        window.removeEventListener("wheel", preventScroll, preventScrollOptions);
        window.removeEventListener("touchmove", preventScroll, preventScrollOptions);

        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isMobile, open]);

  // Update position on scroll and resize (mobile only)
  useEffect(() => {
    if (!isMobile || !isOpen) return;

    let rafId: number | null = null;
    const handleUpdate = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        updatePosition();
        rafId = null;
      });
    };

    // Find all scrollable parent elements
    const getScrollableParents = (element: HTMLElement | null): HTMLElement[] => {
      const parents: HTMLElement[] = [];
      let current = element?.parentElement;

      while (current) {
        const style = window.getComputedStyle(current);
        const overflow = style.overflow + style.overflowY + style.overflowX;
        if (/(auto|scroll)/.test(overflow)) {
          parents.push(current);
        }
        current = current.parentElement;
      }

      return parents;
    };

    const scrollableParents = buttonRef.current ? getScrollableParents(buttonRef.current) : [];
    const allScrollTargets = [window, document, ...scrollableParents];

    // Listen to scroll and resize events
    allScrollTargets.forEach((target) => {
      if (target instanceof Window || target instanceof Document) {
        target.addEventListener("scroll", handleUpdate, { passive: true, capture: true });
      } else {
        target.addEventListener("scroll", handleUpdate, { passive: true });
      }
    });

    window.addEventListener("resize", handleUpdate);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      allScrollTargets.forEach((target) => {
        if (target instanceof Window || target instanceof Document) {
          target.removeEventListener("scroll", handleUpdate, { capture: true } as any);
        } else {
          target.removeEventListener("scroll", handleUpdate);
        }
      });
      window.removeEventListener("resize", handleUpdate);
    };
  }, [isMobile, isOpen, updatePosition]);

  const onChange = useCallback(
    (option: IDealsWithInvestmentDetails | null) => {
      if (!option) return;
      onDealChange(option);
      handleClose();
    },
    [onDealChange]
  );

  return (
    <>
      <MaterialTooltip isShow placement="top" title={dealTitle}>
        <Button
          ref={buttonRef}
          onClick={handleClick}
          sx={{
            minWidth: "87px",
            padding: "0 10px",
            backgroundColor: "#f5f6f7",
            minHeight: "24px",
            maxHeight: "24px"
          }}
          endIcon={open ? <ArrowDropUpIcon fontSize="xl" /> : <ArrowDropDownIcon fontSize="xl" />}
        >
          <Typography
            sx={{
              maxWidth: "140px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontSize: "16px"
            }}
          >
            {dealTitle}
          </Typography>
        </Button>
      </MaterialTooltip>
      {isMobile ? (
        // Mobile: Custom fixed positioning
        isOpen && (
          <>
            {/* Backdrop overlay */}
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 4,
                backgroundColor: "transparent"
              }}
              onClick={handleClose}
            />
            {/* Custom positioned dropdown */}
            <Box
              ref={dropdownRef}
              sx={{
                position: "fixed",
                top: `${position.top}px`,
                left: `${position.left}px`,
                width: `${position.width}px`,
                zIndex: 5,
                background: "white",
                boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                borderRadius: "10px 10px 0 0",
                borderTop: "2px solid #bac7ce",
                overflow: "hidden"
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  <Autocomplete
                    disablePortal={true}
                    options={deals}
                    open
                    popupIcon={""}
                    onChange={(_, option) => onChange(option)}
                    getOptionLabel={(option) => option.title}
                    renderOption={(props, { id, title }) => {
                      return (
                        <li {...props} key={id}>
                          {title}
                        </li>
                      );
                    }}
                    PopperComponent={(props) => (
                      <Box
                        {...(props as any)}
                        sx={{
                          position: "static !important",
                          transform: "none !important",
                          top: "auto !important",
                          left: "auto !important",
                          width: "100%",
                          zIndex: "auto"
                        }}
                      />
                    )}
                    ListboxProps={{
                      sx: {
                        maxHeight: 250,
                        overflow: "auto",
                        padding: 0
                      }
                    }}
                    sx={{
                      width: "100%",
                      "& .MuiAutocomplete-listbox": {
                        padding: 0
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search a deal..."
                        sx={{ "& fieldset": { border: "none" } }}
                        autoFocus
                      />
                    )}
                  />
                </Box>
              </ClickAwayListener>
            </Box>
          </>
        )
      ) : (
        // Desktop: Popper-based
        <Popper
          placement="bottom-start"
          sx={{ zIndex: 5 }}
          open={open}
          anchorEl={anchorEl}
          ref={popperRef}
          modifiers={[
            {
              name: "preventOverflow",
              enabled: true,
              options: {
                boundary: "viewport",
                rootBoundary: "viewport",
                padding: 8
              }
            },
            {
              name: "flip",
              enabled: true,
              options: {
                boundary: "viewport",
                rootBoundary: "viewport"
              }
            },
            {
              name: "offset",
              enabled: true,
              options: {
                offset: [0, 4]
              }
            }
          ]}
          disablePortal={false}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <Box
              sx={{
                border: "none",
                width: 250,
                background: "white",
                boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25);",
                borderRadius: "10px 10px 0 0",
                borderTop: "2px solid #bac7ce",
                overflow: "hidden"
              }}
            >
              <Autocomplete
                disablePortal
                options={deals}
                open
                popupIcon={null}
                onChange={(_, option) => onChange(option)}
                getOptionLabel={(option) => option.title}
                renderOption={(props, { id, title }) => {
                  return (
                    <li {...props} key={id}>
                      {title}
                    </li>
                  );
                }}
                PopperComponent={(props) => (
                  <Box
                    {...(props as any)}
                    sx={{
                      position: "static !important",
                      transform: "none !important",
                      top: "auto !important",
                      left: "auto !important",
                      width: "100%",
                      zIndex: "auto"
                    }}
                  />
                )}
                ListboxProps={{
                  sx: {
                    maxHeight: 250,
                    overflow: "auto",
                    padding: 0
                  }
                }}
                sx={{
                  width: "100%",
                  "& .MuiAutocomplete-listbox": {
                    padding: 0
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search a deal..."
                    sx={{ "& fieldset": { border: "none" } }}
                    autoFocus
                  />
                )}
              />
            </Box>
          </ClickAwayListener>
        </Popper>
      )}
    </>
  );
};
